// src/components/departments/DepartmentSettings.js
import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useDepartment } from '../../contexts/DepartmentContext';
import { useTranslation } from 'react-i18next';

function DepartmentSettings({ department, onDepartmentUpdated }) {
  const { t } = useTranslation('departments');
  const { t: tCommon } = useTranslation('common');

  const { currentUser } = useAuth();
  const { updateDepartmentName, updateDepartmentHours } = useDepartment();

  const [departmentName, setDepartmentName] = useState(department.name);
  const [isEditingName, setIsEditingName] = useState(false);
  const [nameError, setNameError] = useState('');

  const [openingTime, setOpeningTime] = useState(
    department.openingTime instanceof Date
      ? department.openingTime
      : new Date(department.openingTime?.seconds * 1000) || new Date().setHours(9, 0, 0, 0)
  );

  const [closingTime, setClosingTime] = useState(
    department.closingTime instanceof Date
      ? department.closingTime
      : new Date(department.closingTime?.seconds * 1000) || new Date().setHours(17, 0, 0, 0)
  );

  const [autoClockOut, setAutoClockOut] = useState(department.autoClockOutEnabled || false);
  const [preClockInEnabled, setPreClockInEnabled] = useState(department.preClockInEnabled || false);
  const [preClockInMinutes, setPreClockInMinutes] = useState(department.preClockInMinutes || 10);

  const [isSavingHours, setIsSavingHours] = useState(false);
  const [hoursError, setHoursError] = useState('');
  const [hoursSuccess, setHoursSuccess] = useState('');

  const handleEditName = async () => {
    if (!isEditingName) {
      setIsEditingName(true);
      return;
    }

    if (!departmentName.trim()) {
      setNameError(t('errors.nameRequired'));
      return;
    }

    try {
      const success = await updateDepartmentName(department.id, departmentName);
      if (success) {
        setIsEditingName(false);
        setNameError('');
        if (onDepartmentUpdated) {
          onDepartmentUpdated({ ...department, name: departmentName });
        }
      }
    } catch (err) {
      console.error('Error updating department name:', err);
      setNameError(t('errors.nameUpdateFailed'));
    }
  };

  const handleSaveHours = async () => {
    setHoursError('');
    setHoursSuccess('');

    if (!openingTime || !closingTime) {
      setHoursError(t('errors.invalidTimes'));
      return;
    }

    if (openingTime >= closingTime) {
      setHoursError(t('errors.openingBeforeClosing'));
      return;
    }

    if (preClockInEnabled && (preClockInMinutes < 0 || preClockInMinutes > 60)) {
      setHoursError(t('errors.preClockMinutesRange'));
      return;
    }

    setIsSavingHours(true);
    try {
      const success = await updateDepartmentHours(
        department.id,
        openingTime,
        closingTime,
        autoClockOut,
        preClockInEnabled,
        preClockInMinutes
      );

      if (success) {
        setHoursSuccess(t('success.hoursSaved'));
        if (onDepartmentUpdated) {
          onDepartmentUpdated({
            ...department,
            openingTime,
            closingTime,
            autoClockOutEnabled: autoClockOut,
            preClockInEnabled,
            preClockInMinutes
          });
        }
      }
    } catch (err) {
      console.error('Error saving department hours:', err);
      setHoursError(t('errors.hoursSaveFailed'));
    } finally {
      setIsSavingHours(false);
    }
  };

  const formatTimeForInput = (date) => {
    const d = new Date(date);
    return `${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`;
  };

  const parseTimeInput = (timeString, baseDate) => {
    const [hours, minutes] = timeString.split(':').map(Number);
    const date = new Date(baseDate);
    date.setHours(hours, minutes, 0, 0);
    return date;
  };

  return (
    <div className="department-settings">

      {/* Department Details Card */}
      <div className="settings-card">
        <div className="settings-card-header">
          <h2 className="settings-card-title">{t('departmentDetails')}</h2>
        </div>
        <div className="settings-card-body">
          {isEditingName ? (
            <div className="name-edit-row">
              <input
                type="text"
                className="name-input"
                value={departmentName}
                onChange={(e) => setDepartmentName(e.target.value)}
                placeholder={t('departmentName')}
                autoFocus
              />
              <button className="btn btn-primary btn-sm" onClick={handleEditName}>
                {tCommon('buttons.save')}
              </button>
              <button
                className="btn btn-secondary btn-sm"
                onClick={() => {
                  setIsEditingName(false);
                  setDepartmentName(department.name);
                  setNameError('');
                }}
              >
                {tCommon('buttons.cancel')}
              </button>
            </div>
          ) : (
            <div className="name-display-row">
              <span className="name-display-text">{department.name}</span>
              <button className="btn btn-outline btn-sm" onClick={handleEditName}>
                {t('editName')}
              </button>
            </div>
          )}
          {nameError && <div className="field-error">{nameError}</div>}
        </div>
      </div>

      {/* Working Hours Card */}
      <div className="settings-card">
        <div className="settings-card-header">
          <h2 className="settings-card-title">{t('workingHours')}</h2>
        </div>
        <div className="settings-card-body">

          {/* Time Inputs Side by Side */}
          <div className="time-inputs-row">
            <div className="time-field">
              <label className="field-label" htmlFor="opening-time">
                {t('openingTime')}
              </label>
              <input
                id="opening-time"
                type="time"
                className="time-input"
                value={formatTimeForInput(openingTime)}
                onChange={(e) => setOpeningTime(parseTimeInput(e.target.value, openingTime))}
              />
            </div>
            <div className="time-field">
              <label className="field-label" htmlFor="closing-time">
                {t('closingTime')}
              </label>
              <input
                id="closing-time"
                type="time"
                className="time-input"
                value={formatTimeForInput(closingTime)}
                onChange={(e) => setClosingTime(parseTimeInput(e.target.value, closingTime))}
              />
            </div>
          </div>

          <div className="settings-divider" />

          {/* Pre Clock In Toggle */}
          <div className="settings-toggle-row">
            <div className="settings-toggle-info">
              <span className="toggle-label">{t('enablePreClockIn')}</span>
              <span className="toggle-description">{t('preClockInDescription')}</span>
            </div>
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={preClockInEnabled}
                onChange={(e) => setPreClockInEnabled(e.target.checked)}
              />
              <span className="toggle-slider" />
            </label>
          </div>

          {preClockInEnabled && (
            <div className="pre-clock-row">
              <span className="pre-clock-label">{t('allowClockInUpTo')}</span>
              <input
                id="pre-clock-minutes"
                type="number"
                className="minutes-input"
                min="0"
                max="60"
                value={preClockInMinutes}
                onChange={(e) =>
                  setPreClockInMinutes(Math.max(0, Math.min(60, parseInt(e.target.value) || 0)))
                }
              />
              <span className="pre-clock-label">{t('minutesBeforeOpening')}</span>
              <span className="pre-clock-example">{t('preClockInExample')}</span>
            </div>
          )}

          <div className="settings-divider" />

          {/* Auto Clock Out Toggle */}
          <div className="settings-toggle-row">
            <div className="settings-toggle-info">
              <span className="toggle-label">{t('autoClockOutAtClosing')}</span>
              <span className="toggle-description">{t('autoClockOutDescription')}</span>
            </div>
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={autoClockOut}
                onChange={(e) => setAutoClockOut(e.target.checked)}
              />
              <span className="toggle-slider" />
            </label>
          </div>

          {(hoursError || hoursSuccess) && <div className="settings-divider" />}
          {hoursError && <div className="field-error">{hoursError}</div>}
          {hoursSuccess && <div className="field-success">{hoursSuccess}</div>}

          <div className="settings-card-footer">
            <button
              className="btn btn-primary"
              onClick={handleSaveHours}
              disabled={isSavingHours}
            >
              {isSavingHours ? tCommon('saving') : t('saveHours')}
            </button>
          </div>

        </div>
      </div>

    </div>
  );
}

export default DepartmentSettings;